import {
    ISeriesPrimitive,
    IPrimitivePaneRenderer,
    IPrimitivePaneView,
    SeriesAttachedParameter,
    Time,
} from 'lightweight-charts';

export interface LongPositionData {
    entryPrice: number;
    tpPrice: number;
    slPrice: number;
    startLogical: number;
    endLogical: number;
}

class LongPositionRenderer implements IPrimitivePaneRenderer {
    private _data: LongPositionData | null;
    private _p: SeriesAttachedParameter<Time> | null;

    constructor(data: LongPositionData | null, p: SeriesAttachedParameter<Time> | null) {
        this._data = data;
        this._p = p;
    }

    draw(target: any) {
        if (!this._data || !this._p) return;
        
        target.useBitmapCoordinateSpace((scope: any) => {
            const ctx = scope.context;
            const series = this._p!.series;
            const chart = this._p!.chart;

            const x1 = chart.timeScale().logicalToCoordinate(this._data!.startLogical as any);
            const x2 = chart.timeScale().logicalToCoordinate(this._data!.endLogical as any);
            if (x1 === null || x2 === null) return;

            const yEntry = series.priceToCoordinate(this._data!.entryPrice);
            const yTp = series.priceToCoordinate(this._data!.tpPrice);
            const ySl = series.priceToCoordinate(this._data!.slPrice);

            if (yEntry === null || yTp === null || ySl === null) return;

            const left = Math.min(x1, x2) * scope.horizontalPixelRatio;
            const right = Math.max(x1, x2) * scope.horizontalPixelRatio;
            const width = right - left;
            if (width <= 0) return;

            // Draw Target Area (Green)
            const topTp = Math.min(yEntry, yTp) * scope.verticalPixelRatio;
            const bottomTp = Math.max(yEntry, yTp) * scope.verticalPixelRatio;
            const heightTp = bottomTp - topTp;
            
            ctx.fillStyle = 'rgba(16, 185, 129, 0.2)'; // emerald-500 with 0.2 opacity
            ctx.fillRect(left, topTp, width, heightTp);

            // Draw Stop Loss Area (Red)
            const topSl = Math.min(yEntry, ySl) * scope.verticalPixelRatio;
            const bottomSl = Math.max(yEntry, ySl) * scope.verticalPixelRatio;
            const heightSl = bottomSl - topSl;

            ctx.fillStyle = 'rgba(244, 63, 94, 0.2)'; // rose-500 with 0.2 opacity
            ctx.fillRect(left, topSl, width, heightSl);

            // Draw Lines
            ctx.lineWidth = 1 * Math.max(scope.horizontalPixelRatio, scope.verticalPixelRatio);
            
            // Entry Line
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.beginPath();
            ctx.moveTo(left, yEntry * scope.verticalPixelRatio);
            ctx.lineTo(right, yEntry * scope.verticalPixelRatio);
            ctx.stroke();

            // TP Line
            ctx.strokeStyle = 'rgba(16, 185, 129, 0.8)';
            ctx.beginPath();
            ctx.moveTo(left, yTp * scope.verticalPixelRatio);
            ctx.lineTo(right, yTp * scope.verticalPixelRatio);
            ctx.stroke();

            // SL Line
            ctx.strokeStyle = 'rgba(244, 63, 94, 0.8)';
            ctx.beginPath();
            ctx.moveTo(left, ySl * scope.verticalPixelRatio);
            ctx.lineTo(right, ySl * scope.verticalPixelRatio);
            ctx.stroke();
        });
    }
}

class LongPositionPaneView implements IPrimitivePaneView {
    private _data: LongPositionData | null = null;
    private _p: SeriesAttachedParameter<Time> | null = null;

    update(data: LongPositionData | null, p: SeriesAttachedParameter<Time> | null) {
        this._data = data;
        this._p = p;
    }

    renderer(): IPrimitivePaneRenderer | null {
        return new LongPositionRenderer(this._data, this._p);
    }
}

export class LongPositionPlugin implements ISeriesPrimitive<Time> {
    private _paneView: LongPositionPaneView;
    private _data: LongPositionData | null = null;
    private _p: SeriesAttachedParameter<Time> | null = null;

    constructor() {
        this._paneView = new LongPositionPaneView();
    }

    attached(p: SeriesAttachedParameter<Time>) {
        this._p = p;
        this.requestUpdate();
    }

    detached() {
        this._p = null;
    }

    paneViews(): readonly IPrimitivePaneView[] {
        return [this._paneView];
    }

    updateAllViews() {
        this._paneView.update(this._data, this._p);
    }

    setData(data: LongPositionData | null) {
        this._data = data;
        this.requestUpdate();
    }

    requestUpdate() {
        if (this._p) {
            this._p.requestUpdate();
        }
        this.updateAllViews();
    }
}
